import { createStyles, makeStyles } from '@material-ui/core';
import useTranslation from 'next-translate/useTranslation';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { TEXT_ID_BASE } from './StyledComponent';

export const TitleInViewContext = createContext(
    {} as { inViewTitleIndex: number; setInViewTitle: (_: number) => void }
);
export const DrawerLinkContext = createContext({} as (_: string) => void);

interface Props {
    children: React.ReactNode;
}

const useStyles = makeStyles((_) =>
    createStyles({
        legalContainer: { display: 'flex' }
    })
);

export default function LegalProvider({ children }: Props) {
    const classes = useStyles();
    const scrollRef = useRef<HTMLDivElement | null | undefined>();
    const lockSetInViewTitleIndex = useRef(false);

    const { t } = useTranslation('legal');
    const [inViewTitleIndex, setInViewTitleIndex] = useState(-1);

    const onLinkedContentClicked = useCallback((text) => {
        const idx = (t(
            'tableOfContents',
            {},
            { returnObjects: true }
        ) as any).findIndex((el: string) => el.includes(text));

        if (scrollRef.current && idx !== -1) {
            const el = scrollRef.current.querySelector(`#${TEXT_ID_BASE}${idx}`);
            el?.scrollIntoView({ behavior: 'smooth' });
            setInViewTitleIndex(idx);
            lockSetInViewTitleIndex.current = true;
            setTimeout(() => {
                lockSetInViewTitleIndex.current = false;
            }, 1500);
        }
    }, []);
    const setInViewTitle = useCallback((index: number) => {
        if (lockSetInViewTitleIndex.current) return;
        setInViewTitleIndex(index);
    }, []);
    const onScroll = useCallback(() => {
        if (lockSetInViewTitleIndex.current) lockSetInViewTitleIndex.current = false;
    }, []);

    useEffect(() => {
        window.addEventListener('wheel', onScroll);
        window.addEventListener('mousedown', onScroll);
        return () => {
            window.removeEventListener('wheel', onScroll);
            window.removeEventListener('mousedown', onScroll);
        };
    }, []);

    return (
        <TitleInViewContext.Provider value={{ inViewTitleIndex, setInViewTitle }}>
            <DrawerLinkContext.Provider value={onLinkedContentClicked}>
                <div ref={(refs) => (scrollRef.current = refs)} className={classes.legalContainer}>
                    {children}
                </div>
            </DrawerLinkContext.Provider>
        </TitleInViewContext.Provider>
    );
}

export const useLinkDrawerLink = () => {
    const onLinkedContentClicked = useContext(DrawerLinkContext);
    if (!onLinkedContentClicked) {
        throw new Error('you should use context inside provider');
    }

    return onLinkedContentClicked;
};

export const useTitleInViewState = () => {
    const { inViewTitleIndex } = useContext(TitleInViewContext);
    if (inViewTitleIndex === null || inViewTitleIndex === undefined) {
        throw new Error('you should use context inside provider');
    }

    return inViewTitleIndex;
};
export const useTitleInViewAction = () => {
    const { setInViewTitle } = useContext(TitleInViewContext);
    if (!setInViewTitle) {
        throw new Error('you should use context inside provider');
    }

    return setInViewTitle;
};
